if (!customElements.get("wishlist-carousel")) {
  customElements.define(
    "wishlist-carousel",
    class WishlistCarousel extends HTMLElement {
      constructor() {
        super();
        this.customerId = this.dataset.customerId;
        this.numberItems = Number(this.dataset.numberItems || 0);
        this.sectionId = this.dataset.sectionId || "wishlist-carousel";
        this.animateReveal = this.dataset.animateReveal === "true";
        this.pendingCartVariantId = null;
        this.cartRenderObserver = null;
        this.cartRenderTimeout = null;
        this.cartUpdateUnsubscriber = null;
        this.boundHandleClick = this.handleClick.bind(this);
        this.boundHandleWishlistChanged = this.handleWishlistChanged.bind(this);
      }

      connectedCallback() {
        this.addEventListener("click", this.boundHandleClick);
        this.addEventListener(
          "wishlist:changed",
          this.boundHandleWishlistChanged,
        );
        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS?.cartUpdate
        ) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.handleCartUpdate.bind(this),
          );
        }
        void this.init();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.boundHandleClick);
        this.removeEventListener(
          "wishlist:changed",
          this.boundHandleWishlistChanged,
        );
        if (typeof this.cartUpdateUnsubscriber === "function") {
          this.cartUpdateUnsubscriber();
        }
        this.stopWatchingCartRender();
      }

      formatCreatedAt(date = new Date()) {
        const pad = (value) => String(value).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      }

      normalizeWishlistItem(item) {
        if (!item || typeof item !== "object") return item;

        return {
          ...item,
          created_at: item.created_at || this.formatCreatedAt(),
        };
      }

      normalizeWishlistItems(items) {
        return Array.isArray(items)
          ? items.map((item) => this.normalizeWishlistItem(item))
          : [];
      }

      parseCreatedAt(value) {
        if (typeof value !== "string") return 0;

        const [datePart = "", timePart = ""] = value.trim().split(" ");
        const [year, month, day] = datePart.split("-").map(Number);
        const [hours = 0, minutes = 0, seconds = 0] = timePart
          .split(":")
          .map(Number);

        if ([year, month, day].some((part) => Number.isNaN(part))) {
          return 0;
        }

        return new Date(
          year,
          (month || 1) - 1,
          day || 1,
          hours || 0,
          minutes || 0,
          seconds || 0,
        ).getTime();
      }

      loadLocalWishlists() {
        const wishlists = localStorage.getItem("wishlists");
        if (!wishlists) {
          return [];
        }

        try {
          const parsedWishlists = JSON.parse(wishlists);
          return Array.isArray(parsedWishlists) ? parsedWishlists : [];
        } catch (error) {
          console.error("Failed to parse local wishlists", error);
          return [];
        }
      }

      async fetchJson(url, body) {
        console.log("body", body);
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`Request failed (${response.status}) for ${url}`);
        }

        const data = await response.json();
        if (data.failed) {
          throw new Error(data.error_message || `Endpoint error for ${url}`);
        }

        return data;
      }

      async getCartVariantIds() {        
        const response = await fetch(`${Shopify.routes.root}cart.js`, {
            headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed (${response.status}) for /cart.js`);
        }

        const cart = await response.json();
        return Array.isArray(cart?.items)
          ? cart.items
              .map((item) => Number(item?.variant_id))
              .filter((variantId) => !Number.isNaN(variantId))
          : [];
      }

      async getItems() {
        const cartIds = await this.getCartVariantIds();

        console.log("cartIds", cartIds);

        if (this.customerId) {
          const response = await this.fetchJson(
            "https://rarewaves-wishlist.etempashopifyapps-server-b.co.uk/endpoints/get_wishlist_subsection.php",
            {
              shopifyCustomerId: this.customerId,
              cartIds,
              numberItems: this.numberItems,
            },
          );

          const remoteItems = Array.isArray(response)
            ? response
            : Array.isArray(response?.items)
              ? response.items
              : Array.isArray(response?.wishlists_items)
                ? response.wishlists_items
                : [];

          return this.normalizeWishlistItems(remoteItems);
        }

        const items = this.loadLocalWishlists()
          .flatMap((wishlist) =>
            Array.isArray(wishlist?.items) ? wishlist.items : [],
          )
          .filter((item) => {
            const variantId = Number(item?.variant_id);
            if (Number.isNaN(variantId)) {
              return true;
            }

            return !cartIds.includes(variantId);
          })
          .map((item) => this.normalizeWishlistItem(item))
          .sort(
            (a, b) =>
              this.parseCreatedAt(b.created_at) -
              this.parseCreatedAt(a.created_at),
          );

        return items.slice(0, this.numberItems);
      }

      handleClick(event) {
        const submitButton = event.target.closest(
          'product-form [type="submit"]',
        );
        if (!submitButton || !this.contains(submitButton)) {
          return;
        }

        const productForm = submitButton.closest("product-form");
        const variantId = productForm?.querySelector('[name="id"]')?.value;
        this.pendingCartVariantId = variantId ? String(variantId) : null;
      }

      handleCartUpdate(event = {}) {
        if (event?.source !== "product-form") {
          return;
        }

        const eventVariantId = String(event?.productVariantId ?? "");
        if (!eventVariantId || eventVariantId !== this.pendingCartVariantId) {
          return;
        }

        this.watchForCartRender();
      }

      handleWishlistChanged(event) {
        if (event?.detail?.action !== "item_removed") {
          return;
        }

        if (!this.contains(event.target)) {
          return;
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            void this.init();
          });
        });
      }

      getCartRenderTargets() {
        return [
          document.getElementById("CartDrawer"),
          document.getElementById("cart-notification"),
          document.getElementById("main-cart-items"),
        ].filter(Boolean);
      }

      stopWatchingCartRender() {
        if (this.cartRenderObserver) {
          this.cartRenderObserver.disconnect();
          this.cartRenderObserver = null;
        }

        if (this.cartRenderTimeout) {
          window.clearTimeout(this.cartRenderTimeout);
          this.cartRenderTimeout = null;
        }
      }

      queueRerenderAfterCartUpdate() {
        this.stopWatchingCartRender();
        this.pendingCartVariantId = null;

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            void this.init();
          });
        });
      }

      watchForCartRender() {
        this.stopWatchingCartRender();

        const targets = this.getCartRenderTargets();
        if (targets.length === 0) {
          this.queueRerenderAfterCartUpdate();
          return;
        }

        this.cartRenderObserver = new MutationObserver((mutations) => {
          const hasRelevantMutation = mutations.some(
            (mutation) =>
              mutation.type === "childList" &&
              (mutation.addedNodes.length > 0 ||
                mutation.removedNodes.length > 0),
          );

          if (!hasRelevantMutation) {
            return;
          }

          this.queueRerenderAfterCartUpdate();
        });

        targets.forEach((target) => {
          this.cartRenderObserver.observe(target, {
            childList: true,
            subtree: true,
          });
        });

        this.cartRenderTimeout = window.setTimeout(() => {
          this.queueRerenderAfterCartUpdate();
        }, 2000);
      }

      createListItem(index, cardElement) {
        const listItem = document.createElement("li");
        listItem.id = `Slide-${this.sectionId}-${index + 1}`;
        listItem.className = [
          "product-slider--product-card-container",
          this.animateReveal ? "scroll-trigger animate--slide-in" : "",
        ]
          .filter(Boolean)
          .join(" ");

        if (this.animateReveal) {
          listItem.dataset.cascade = "";
          listItem.style.setProperty("--animation-order", String(index + 1));
        }

        listItem.append(cardElement);
        return listItem;
      }

      async fetchProductCard(item, index) {
        const handle = item?.product_handle;
        if (!handle) return null;

        const sectionUrl = new URL(
          `/products/${encodeURIComponent(handle)}`,
          window.location.origin,
        );
        sectionUrl.searchParams.set("section_id", "wishlist-product-card");
        sectionUrl.searchParams.set("variant", String(item?.variant_id ?? ""));
        sectionUrl.searchParams.set(
          "type",
          (Math.random() + 1).toString(36).substring(7),
        );

        const response = await fetch(
          `${sectionUrl.pathname}${sectionUrl.search}`,
        );
        const html = await response.text();
        if (!html || html.includes("wishlist-product-card-empty path:/404")) {
          return null;
        }

        const tmpHTML = document.createElement("div");
        tmpHTML.innerHTML = html;
        const productCardSection = tmpHTML.querySelector(
          "[data-wishlist-product-card]",
        );
        if (!productCardSection) return null;

        const cardElement = Array.from(productCardSection.children).find(
          (child) => child.nodeType === Node.ELEMENT_NODE,
        );
        if (!cardElement) return null;

        return this.createListItem(index, cardElement.cloneNode(true));
      }

      updateSliderChromeVisibility(hasItems) {
        const productSlider = this.closest("product-slider");
        if (!productSlider) return;

        const buttonContainer = productSlider.querySelector(
          "[data-button-container]",
        );
        const counter = productSlider.querySelector(".slider-counter");

        if (buttonContainer) {
          buttonContainer.classList.toggle("hidden", !hasItems);
        }
        if (counter) {
          counter.classList.toggle("hidden", !hasItems);
        }
      }

      async renderItems(items) {
        this.innerHTML = "";

        if (!Array.isArray(items) || items.length === 0) {
          this.updateSliderChromeVisibility(false);
          return;
        }

        const productCards = await Promise.all(
          items.map((item, index) => this.fetchProductCard(item, index)),
        );

        const fragment = document.createDocumentFragment();
        productCards.filter(Boolean).forEach((listItem) => {
          fragment.append(listItem);
        });

        this.append(fragment);
        this.updateSliderChromeVisibility(this.children.length > 0);
      }

      toggleCartSections(hasItems) {
        const wishlistSection = document.querySelector(
          "[data-wishlist-section]",
        );
        const relatedSection = document.querySelector(
          "[data-cart-related-products]",
        );

        if (wishlistSection) {
          wishlistSection.style.display = hasItems ? "" : "none";
        }
        if (relatedSection) {
          relatedSection.style.display = hasItems ? "none" : "";
        }
      }

      async init() {
        try {
          const items = await this.getItems();
          await this.renderItems(items);
          this.toggleCartSections(
            Array.isArray(items) && items.length > 0,
          );
        } catch (error) {
          console.error("Error loading wishlist carousel", error);
          this.innerHTML = "";
          this.updateSliderChromeVisibility(false);
          this.toggleCartSections(false);
        }
      }
    },
  );
}
